import { browser } from '$app/environment';
import { writable, get, derived } from 'svelte/store';
import { ensureGrowthBookInitialized, getAllFeaturesSync, evaluateFeature } from '$lib/services/growthbook.js';

// In-memory cache with timestamp to avoid tight re-evaluations
let lastRefresh = 0;
let onMap = {};
const MIN_REFRESH_INTERVAL_MS = 5_000; // 5s, matches prior behavior

function createFeatureFlagsStore() {
  const { subscribe, set, update } = writable({});

  return {
    subscribe,

    // Replace entire flags map
    setAll(flags) {
      const incoming = flags || {};
      // Derive booleans best-effort when setting wholesale
      onMap = Object.fromEntries(
        Object.entries(incoming).map(([k, v]) => [k, typeof v === 'boolean' ? v : !!v])
      );
      set(incoming);
    },

    // Get single flag value (sync read from store)
    get(key, defaultValue = null) {
      const map = get({ subscribe });
      return key in map ? map[key] : defaultValue;
    },

    // Convenience: boolean flags
    isOn(key) {
      // Se non siamo nel browser, restituisci sempre false (SSR safe)
      if (!browser) return false;
      
      // Prefer last evaluated boolean from GrowthBook
      if (key in onMap) return !!onMap[key];
      // Fallback: coerce current cached value
      return !!this.get(key, false);
    },

    // Snapshot of all flags
    getAll() {
      return { ...get({ subscribe }) };
    },

    // Hydrate from GrowthBook (once initialized)
    async refresh(force = false) {
      if (!browser) return {};
      const now = Date.now();
      if (!force && now - lastRefresh < MIN_REFRESH_INTERVAL_MS) {
        return this.getAll();
      }

      const gb = await ensureGrowthBookInitialized();
      if (!gb) {
        return this.getAll();
      }

      const definitions = getAllFeaturesSync() || {};
      const mapped = {};
      const newOnMap = {};
      const keys = Object.keys(definitions);
      for (const key of keys) {
        try {
          const info = evaluateFeature(key) || {};
          mapped[key] = 'value' in info ? info.value : null;
          newOnMap[key] = !!info.on;
        } catch (e) {
          mapped[key] = null;
          newOnMap[key] = false;
        }
      }

      lastRefresh = now;
      onMap = newOnMap;
      set(mapped);
      return mapped;
    }
  };
}

export const featureFlags = createFeatureFlagsStore();

// Kill-switch reattivo per le notifiche push web (nasconde toggle + blocca registrazione).
// Default OFF: se il flag non è definito su GrowthBook, la feature è spenta.
export const webPushEnabled = derived(
  featureFlags,
  () => {
    if (!browser) return false;
    return featureFlags.isOn('web-push-notifications');
  }
);
